// Design frames: wireframes/15-properties-m3.html (15.1 to 15.16).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMatch, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ArchThumb,
  BottomSheet,
  Button,
  Chip,
  Fab,
  List,
  ListItem,
  Notice,
  SearchField,
  Skeleton,
  StateScreen,
  Tag,
  iconAdd,
  useSnackbar,
} from '@sauda/ui/kit';
import { Screen, useChrome } from '../../app/chrome.js';
import { useAuth } from '../../auth/AuthContext.js';
import { api } from '../../lib/api.js';
import { formatRupees } from '../../lib/money.js';
import { daysAgo, isQuiet, isStale } from '../../lib/property-list.js';
import { whatsappLink } from '../../lib/share-text.js';
import type { Branch, PropertyView, ShareInfo } from '../../lib/types.js';
import { StatusSheet } from './StatusSheet.js';

type Status = PropertyView['status'];
const TONE: Record<Status, 'ok' | 'warn' | 'neutral'> = {
  available: 'neutral',
  on_hold: 'neutral',
  let_out_or_sold: 'neutral',
  archived: 'neutral',
};

/**
 * The property list: a search field, filter chips (Available, Mine, Rent, Sale, On hold, Branch),
 * and a row for each property with its photo, a status and a Share button.
 */
export function PropertiesScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const navigate = useNavigate();
  const snack = useSnackbar();
  const open = useMatch('/properties/:id')?.params['id'];
  const [query, setQuery] = useState('');
  const [available, setAvailable] = useState(true);
  const [mine, setMine] = useState(false);
  const [kind, setKind] = useState<'rent' | 'sale' | null>(null);
  const [hold, setHold] = useState(false);
  const [branch, setBranch] = useState<string | null>(null);
  const [branchSheet, setBranchSheet] = useState(false);
  const [quiet, setQuiet] = useState(false);
  const [stale, setStale] = useState(false);
  const [marking, setMarking] = useState<PropertyView | null>(null);
  const cache = useQueryClient();
  const now = Date.now();
  const properties = useQuery({
    queryKey: ['properties', auth.agencyId],
    queryFn: () => api<PropertyView[]>('/properties'),
  });
  const branches = useQuery({
    queryKey: ['branches', auth.agencyId],
    queryFn: () => api<Branch[]>('/branches'),
    enabled: auth.allowed('branches.view'),
  });
  const share = useMutation({
    mutationFn: (id: string) => api<ShareInfo>(`/properties/${id}/share`),
    onSuccess: (info) => window.open(whatsappLink(info.text), '_blank', 'noopener'),
    onError: () => snack({ text: t('today.actionFailed') }),
  });
  const all = properties.data ?? [];
  const needle = query.trim().toLowerCase();
  const shown = all.filter(
    (p) =>
      (available ? p.status === 'available' : p.status !== 'archived') &&
      (!hold || p.status === 'on_hold') &&
      (!mine || p.ownerMemberId === auth.current?.memberId) &&
      (kind === null || p.kind === kind) &&
      (branch === null || p.branchId === branch) &&
      (!quiet || isQuiet(p, now)) &&
      (!stale || isStale(p, now)) &&
      (needle === '' ||
        [p.title, p.society, p.locality].some((v) => (v ?? '').toLowerCase().includes(needle))),
  );
  const canAdd = auth.allowed('properties.write') && !chrome.readOnly;
  const branchList = branches.data ?? [];
  return (
    <Screen title={t('nav.properties')} large>
      <SearchField
        label={t('property.search')}
        placeholder={t('property.searchHint')}
        value={query}
        onChange={setQuery}
      />
      <div className="chips-row" role="group" aria-label={t('property.filter')}>
        <Chip selected={available} onClick={() => setAvailable(!available)}>
          {t('property.status_available')}
        </Chip>
        <Chip selected={mine} onClick={() => setMine(!mine)}>
          {t('property.mine')}
        </Chip>
        <Chip selected={kind === 'rent'} onClick={() => setKind(kind === 'rent' ? null : 'rent')}>
          {t('property.kind_rent')}
        </Chip>
        <Chip selected={kind === 'sale'} onClick={() => setKind(kind === 'sale' ? null : 'sale')}>
          {t('property.kind_sale')}
        </Chip>
        <Chip selected={hold} onClick={() => setHold(!hold)}>
          {t('property.status_on_hold')}
        </Chip>
        <Chip selected={quiet} onClick={() => setQuiet(!quiet)}>
          {t('property.quiet')}
        </Chip>
        <Chip selected={stale} onClick={() => setStale(!stale)}>
          {t('property.stale')}
        </Chip>
        {branchList.length > 1 ? (
          <Chip selected={branch !== null} onClick={() => setBranchSheet(true)}>
            {branchList.find((b) => b.id === branch)?.name ?? t('property.branch')}
          </Chip>
        ) : null}
      </div>
      {properties.isPending ? <Skeleton height={72} /> : null}
      {properties.isError ? (
        <Notice
          tone="error"
          action={
            <Button variant="text" onClick={() => void properties.refetch()}>
              {t('app.retry')}
            </Button>
          }
        >
          {t('today.loadFailed')}
        </Notice>
      ) : null}
      {properties.isSuccess && shown.length === 0 ? (
        <StateScreen
          kind="empty"
          compact
          title={all.length === 0 ? t('property.none') : t('property.noneHere')}
        />
      ) : null}
      {shown.length === 0 ? null : (
        <List label={t('nav.properties')}>
          {shown.map((p) => {
            const amount = p.kind === 'rent' ? p.rentPaise : p.pricePaise;
            const cover = p.photos.find((photo) => photo.status === 'ready');
            return (
              <ListItem
                key={p.id}
                selected={open === p.id}
                title={p.title}
                leading={<ArchThumb src={cover?.thumbUrl ?? null} seed={p.id} />}
                supporting={[
                  amount === null ? null : formatRupees(amount),
                  p.locality,
                  p.lastEnquiryAt == null
                    ? t('property.noEnquiry')
                    : t('property.lastEnquiry', { count: daysAgo(p.lastEnquiryAt, now) }),
                  t('property.enquiriesMonth', { count: p.enquiriesThisMonth ?? 0 }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
                tags={<Tag tone={TONE[p.status]}>{t(`property.status_${p.status}`)}</Tag>}
                onClick={() => void navigate(`/properties/${p.id}`)}
                action={
                  p.status === 'archived' ? undefined : (
                    <>
                      {canAdd && p.canEdit ? (
                        <Button variant="text" onClick={() => setMarking(p)}>
                          {t('property.mark')}
                        </Button>
                      ) : null}
                      <Button
                        variant="tonal"
                        loading={share.isPending && share.variables === p.id}
                        onClick={() => share.mutate(p.id)}
                      >
                        {t('property.shareShort')}
                      </Button>
                    </>
                  )
                }
              />
            );
          })}
        </List>
      )}
      {canAdd ? (
        <div className="fab-slot">
          <Fab
            icon={iconAdd}
            label={t('property.addShort')}
            extended
            onClick={() => void navigate('/properties/new')}
          />
        </div>
      ) : null}
      <StatusSheet
        property={marking}
        onClose={() => setMarking(null)}
        onDone={() => void cache.invalidateQueries({ queryKey: ['properties'] })}
      />
      <BottomSheet
        open={branchSheet}
        onClose={() => setBranchSheet(false)}
        label={t('property.branch')}
        title={t('property.branch')}
        closeLabel={t('app.close')}
      >
        <List label={t('property.branch')}>
          <ListItem
            title={t('property.allBranches')}
            selected={branch === null}
            onClick={() => {
              setBranch(null);
              setBranchSheet(false);
            }}
          />
          {branchList.map((b) => (
            <ListItem
              key={b.id}
              title={b.name}
              selected={branch === b.id}
              onClick={() => {
                setBranch(b.id);
                setBranchSheet(false);
              }}
            />
          ))}
        </List>
      </BottomSheet>
    </Screen>
  );
}
